/** When this browser last autosaved each draft. The API has no draft timestamp. */

const STORAGE_KEY = 'fletch:draft:last-saved-at';

/** Keeps the entry list from growing without bound as drafts accumulate. */
const MAX_ENTRIES = 200;

type SavedAtMap = Record<string, number>;

const readMap = (): SavedAtMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const entries = Object.entries(parsed as Record<string, unknown>).filter(
      (entry): entry is [string, number] => typeof entry[1] === 'number' && Number.isFinite(entry[1]),
    );
    return Object.fromEntries(entries);
  } catch {
    // Access can fail (privacy mode) or the value can be corrupt — start over.
    return {};
  }
};

/** The last time this browser saved the draft, or `null` if it never has. */
export const getDraftLastSavedAt = (draftVersionId: string): Date | null => {
  if (!draftVersionId) return null;
  const timestamp = readMap()[draftVersionId];
  return typeof timestamp === 'number' ? new Date(timestamp) : null;
};

export const setDraftLastSavedAt = (draftVersionId: string, savedAt: Date): void => {
  if (!draftVersionId) return;

  try {
    const map = readMap();
    map[draftVersionId] = savedAt.getTime();

    const ids = Object.keys(map);
    if (ids.length > MAX_ENTRIES) {
      // Drop the oldest saves first; they are the least likely to be reopened.
      ids
        .sort((a, b) => map[a] - map[b])
        .slice(0, ids.length - MAX_ENTRIES)
        .forEach((id) => delete map[id]);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // A missing timestamp only degrades the header label, so failing is fine.
  }
};
